import { useQuery } from '@tanstack/react-query';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { formatNumber } from '../../utils/formatters';
import { api } from '../../utils/apiClient';
import { isMobileChartViewport } from '../../utils/chartResponsive';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip, Legend);

interface CustomerPurchaseBehaviorDashboardState {
  success: boolean;
  data: PurchaseBehaviorRow[];
}

export interface PurchaseBehaviorRow {
  YearMonth: number;
  Purchase_1: number;
  Purchase_2: number;
  Purchase_3: number;
  Purchase_4: number;
  Purchase_5: number;
  Purchase_6Plus: number;
  NoSubscriptionCount: number | null;
}

export type ActiveBucketKey =
  | 'Purchase_1'
  | 'Purchase_2'
  | 'Purchase_3'
  | 'Purchase_4'
  | 'Purchase_5'
  | 'Purchase_6Plus'
  | 'NoSubscriptionCount';

export type StackedPurchaseViewMode = 'count' | 'percent';

export const numeric = (value: unknown): number => Number(value || 0);

// \u062A\u0628\u062F\u06CC\u0644 \u0627\u0631\u0642\u0627\u0645 \u0644\u0627\u062A\u06CC\u0646 \u0628\u0647 \u0641\u0627\u0631\u0633\u06CC \u0628\u0631\u0627\u06CC \u0646\u0645\u0627\u06CC\u0634 \u062F\u0631 \u0646\u0645\u0648\u062F\u0627\u0631\u0647\u0627
export const toPersianDigits = (input: string | number): string =>
  String(input).replace(/\d/g, (digit) => '\u06F0\u06F1\u06F2\u06F3\u06F4\u06F5\u06F6\u06F7\u06F8\u06F9'[Number(digit)]);

export const formatSignedPercent = (value: number, decimals: number = 1): string => {
  const abs = Math.abs(value).toFixed(decimals);
  const sign = value < 0 ? '-' : '+';
  return `\u200E${sign}${toPersianDigits(abs)}%`;
};

export const formatPercentTick = (value: number): string => {
  const abs = Math.abs(value).toFixed(0);
  const sign = value < 0 ? '-' : '';
  return `\u200E${sign}${toPersianDigits(abs)}%`;
};

const monthNamesFa = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
];

export const toMonthLabel = (yearMonth: number): string => {
  const value = String(yearMonth || '');
  if (value.length < 6) return value;
  const monthIndex = Number(value.slice(4, 6)) - 1;
  return monthNamesFa[monthIndex] || value;
};

export const toGrowthLabel = (previousYearMonth: number, currentYearMonth: number): string => {
  // برچسب گذار: ماه انتهایی ← ماه قبلی (مثلاً «اردیبهشت به فروردین»)
  return `${toMonthLabel(currentYearMonth)} به ${toMonthLabel(previousYearMonth)}`;
};

export const getGrowthPercent = (current: number, previous: number): number | null => {
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / previous) * 100;
};

// خط صفر فقط توسط zeroBaselinePlugin (خط‌چین theme-aware) رسم می‌شود تا تنها یک خط صفر داشته باشیم.
export const getZeroLineGrid = (baseGrid: any) => ({
  ...baseGrid,
  display: false,
});

export const getSymmetricAxisBounds = (
  values: Array<number | null | undefined>,
  fallbackAbsMax: number = 10
) => {
  const validValues = values.filter((value): value is number => value !== null && value !== undefined);
  const rawMaxAbs = validValues.length ? Math.max(...validValues.map(value => Math.abs(value))) : fallbackAbsMax;
  const boundedAbs = Math.max(rawMaxAbs, fallbackAbsMax);
  const padding = Math.max(boundedAbs * 0.15, 2);
  return {
    min: -(boundedAbs + padding),
    max: boundedAbs + padding,
    stepSize: Math.max(Math.round((boundedAbs + padding) / 6), 1),
  };
};

export const getPositiveAxisBounds = (
  values: Array<number | null | undefined>,
  fallbackMax: number = 10
) => {
  const validValues = values.filter((value): value is number => value !== null && value !== undefined);
  const rawMax = validValues.length ? Math.max(...validValues) : fallbackMax;
  const boundedMax = Math.max(rawMax, fallbackMax);
  const padding = Math.max(boundedMax * 0.15, 2);
  return {
    min: 0,
    max: boundedMax + padding,
    stepSize: Math.max(Math.round((boundedMax + padding) / 6), 1),
  };
};

export const calculateLinearRegression = (values: Array<number | null | undefined>): Array<number | null> => {
  const points = values
    .map((value, index) => ({ x: index, y: value }))
    .filter((point): point is { x: number; y: number } => point.y !== null && point.y !== undefined);

  if (points.length === 0) {
    return values.map(() => null);
  }

  if (points.length === 1) {
    const firstValue = points[0].y;
    return values.map((value) => (value === null || value === undefined ? null : firstValue));
  }

  const sumX = points.reduce((sum, point) => sum + point.x, 0);
  const sumY = points.reduce((sum, point) => sum + point.y, 0);
  const sumXY = points.reduce((sum, point) => sum + (point.x * point.y), 0);
  const sumXX = points.reduce((sum, point) => sum + (point.x * point.x), 0);
  const n = points.length;
  const denominator = (n * sumXX) - (sumX * sumX);
  const slope = denominator === 0 ? 0 : ((n * sumXY) - (sumX * sumY)) / denominator;
  const intercept = (sumY - (slope * sumX)) / n;

  return values.map((value, index) => (
    value === null || value === undefined ? null : (slope * index) + intercept
  ));
};

const MOBILE_GROUP_LABEL_SHORT: Record<string, string> = {
  'اشتراک‌گیری': 'اشتراک',
  'بدون اشتراک': 'بدون اشتراک',
  '۱ بار خرید (مشتری جدید)': '۱ خرید',
  '۲ بار خرید': '۲ خرید',
  '۳ بار خرید': '۳ خرید',
  '۴ بار خرید': '۴ خرید',
  '۵ بار خرید': '۵ خرید',
  '۶ بار خرید و بیشتر': '۶+ خرید',
  'مشتریان بازگشتی': 'بازگشتی',
  'کل سفارش‌ها': 'کل',
};

export const getMobileGroupLabels = (labels: string[]): string[] => {
  if (!isMobileChartViewport()) return labels;
  return labels.map((label) => MOBILE_GROUP_LABEL_SHORT[label] ?? label);
};

export interface CustomerPurchaseBehaviorChartProps {
  branchId?: number;
  purchaseData?: PurchaseBehaviorRow[];
  onRetry?: () => void;
}

export const getControllerButtonClass = (isActive: boolean, withMinWidth: boolean = false): string => {
  const baseClass = 'px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-text-primary border border-border-primary rounded-lg transition-colors whitespace-nowrap flex-shrink-0';
  const widthClass = withMinWidth ? ' min-w-0 sm:min-w-[120px] lg:min-w-[132px] text-center' : '';
  const stateClass = isActive
    ? ' bg-bg-tertiary border-border-secondary'
    : ' bg-bg-secondary hover:bg-bg-tertiary';
  return `${baseClass}${widthClass}${stateClass}`;
};

export const makeStripePattern = (ctx: CanvasRenderingContext2D, color: string) => {
  const canvas = document.createElement('canvas');
  canvas.width = 8;
  canvas.height = 8;
  const patternCtx = canvas.getContext('2d');
  if (!patternCtx) return color;

  patternCtx.clearRect(0, 0, canvas.width, canvas.height);
  patternCtx.strokeStyle = color;
  patternCtx.lineWidth = 2;
  patternCtx.beginPath();
  patternCtx.moveTo(0, 8);
  patternCtx.lineTo(8, 0);
  patternCtx.stroke();

  return ctx.createPattern(canvas, 'repeat') || color;
};

export const valueLabelPlugin = {
  id: 'valueLabelPlugin',
  afterDatasetsDraw(chart: any) {
    const { ctx } = chart;
    const pluginOptions = chart?.options?.plugins?.valueLabelPlugin || {};
    const isEnabled = Boolean(pluginOptions.enabled ?? true);
    if (!isEnabled) return;
    const minPixelHeight = Number(pluginOptions.minPixelHeight ?? 0);
    const minAbsValue = Number(pluginOptions.minAbsValue ?? 0);
    const percentDecimals = Number(pluginOptions.percentDecimals ?? 0);
    const avoidOverlap = Boolean(pluginOptions.avoidOverlap ?? false);
    const showBackground = Boolean(pluginOptions.showBackground ?? true);
    const labelPosition = String(pluginOptions.labelPosition ?? 'edge');
    // اگر ارتفاع میله از این مقدار (پیکسل) کمتر باشد، برچسب بیرون و بالای میله نمایش داده می‌شود (۰ = غیرفعال)
    const shortBarThreshold = Number(pluginOptions.shortBarThreshold ?? 0);
    // رنگ مشکی برای همه‌ی برچسب‌ها (مطابق استایل جدول عملکرد ماهانه)
    const textColor = String(pluginOptions.textColor ?? '#111827');
    const fontSize = Number(pluginOptions.fontSize ?? 10);
    const occupiedBoxes: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];

    ctx.save();
    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // جهت متن روی بوم را LTR قفل می‌کنیم تا علامت منفی سمت چپِ عدد بیفتد (نه راست) در محیط RTL
    ctx.direction = 'ltr';

    chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;

      meta.data.forEach((bar: any, i: number) => {
        if (typeof bar?.base !== 'number' || typeof bar?.y !== 'number') return;
        const rawValue = dataset.data[i];
        if (rawValue === null || rawValue === undefined) return;
        const value = Number(rawValue);
        if (!Number.isFinite(value)) return;
        const rawLabelValue = Array.isArray(dataset.valueLabelData) ? dataset.valueLabelData[i] : value;
        const labelValue = Number(rawLabelValue);
        if (!Number.isFinite(labelValue)) return;
        const isLabelPercent = typeof dataset.valueLabelIsPercent === 'boolean'
          ? dataset.valueLabelIsPercent
          : Boolean(dataset.isPercent);
        const thresholdValue = isLabelPercent ? labelValue : value;
        if (Math.abs(thresholdValue) < minAbsValue) return;
        const barHeight = Math.abs(bar.base - bar.y);
        // میله کوتاه: برچسب داخل میله جا نمی‌شود؛ بیرون و بالای میله نمایش داده می‌شود
        const isShortBar = shortBarThreshold > 0 && barHeight < shortBarThreshold;
        // میله‌های خیلی کوتاه را فقط زمانی رد کن که حالت نمایش بالای میله فعال نباشد
        if (!isShortBar && barHeight < minPixelHeight) return;

        const rawText = isLabelPercent ? `${labelValue.toFixed(percentDecimals)}%` : formatNumber(labelValue);
        // نمایش اعداد با ارقام فارسی
        const text = toPersianDigits(rawText);
        const x = bar.x;
        // میله کوتاه: بالای میله (مثبت) یا زیر آن (منفی). در غیر این صورت طبق labelPosition.
        const y = isShortBar
          ? (bar.y + (value >= 0 ? -10 : 10))
          : (labelPosition === 'center' ? (bar.base + bar.y) / 2 : (bar.y + (value >= 0 ? 12 : -8)));
        const textWidth = ctx.measureText(text).width;
        const boxWidth = textWidth + 6;
        const boxHeight = 14;
        const box = {
          x1: x - boxWidth / 2,
          y1: y - 11,
          x2: x + boxWidth / 2,
          y2: y - 11 + boxHeight,
        };

        if (avoidOverlap) {
          const overlap = occupiedBoxes.some((placed) => (
            box.x1 < placed.x2 && box.x2 > placed.x1 && box.y1 < placed.y2 && box.y2 > placed.y1
          ));
          if (overlap) return;
          occupiedBoxes.push(box);
        }

        if (showBackground) {
          ctx.fillStyle = 'rgba(17, 24, 39, 0.78)';
          ctx.fillRect(box.x1, box.y1, boxWidth, boxHeight);
        }
        // هاله‌ی سفید ملایم پشت متن مشکی برای خوانایی روی رنگ میله
        ctx.fillStyle = textColor;
        ctx.shadowColor = showBackground ? 'transparent' : 'rgba(255, 255, 255, 0.6)';
        ctx.shadowBlur = showBackground ? 0 : 3;
        ctx.fillText(text, x, y);
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      });
    });

    ctx.restore();
  },
};

export const activeSeries: Array<{ key: ActiveBucketKey; label: string; striped?: boolean }> = [
  { key: 'NoSubscriptionCount', label: 'بدون اشتراک', striped: true },
  { key: 'Purchase_1', label: '۱ بار خرید (مشتری جدید)' },
  { key: 'Purchase_2', label: '۲ بار خرید' },
  { key: 'Purchase_3', label: '۳ بار خرید' },
  { key: 'Purchase_4', label: '۴ بار خرید' },
  { key: 'Purchase_5', label: '۵ بار خرید' },
  { key: 'Purchase_6Plus', label: '۶ بار خرید و بیشتر' },
];

export const getSortedPurchaseData = (data: PurchaseBehaviorRow[] | undefined): PurchaseBehaviorRow[] => {
  if (!Array.isArray(data)) return [];
  return [...data].sort((a, b) => numeric(a.YearMonth) - numeric(b.YearMonth)).slice(-12);
};

export const totalMonthlyInvoices = (rows: PurchaseBehaviorRow[]): number[] => (
  rows.map((item) => activeSeries.reduce((sum, series) => sum + numeric(item[series.key]), 0))
);

export const useCustomerPurchaseBehaviorData = (
  branchId?: number,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['customer-purchase-behavior', branchId],
    enabled: (options?.enabled ?? true) && !!branchId,
    queryFn: async () => {
      const response = await api.get<CustomerPurchaseBehaviorDashboardState>(
        `/customer-purchase-behavior/${branchId}?months=12`
      );

      if (!response.success || !Array.isArray(response.data)) {
        throw new Error('Invalid customer purchase behavior response');
      }

      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};
